import { t } from 'i18next'
import { Box, Typography } from '@mui/material'

import {
  FILTER_OPERATORS,
  FILTER_SELECT_TYPES,
  MOBILE_FILTER_TYPES,
} from '@hapana/shared/web/components/shared/table/table.constants'
import TableStatus from '@hapana/shared/web/components/shared/table-status/table-status.component'
import {
  AVATAR_SIZE,
  AVATAR_TYPES,
  AVATAR_VARIANT,
} from '@hapana/shared/web/components/shared/avatar/avatar.constants'
import Avatar from '@hapana/shared/web/components/shared/avatar/single-avatar.component'
import { commonStyles } from '@hapana/shared/web/styles/common.styles'
import { StaffUserAccessLevel, StaffUserStatus } from '@hapana/shared/web/services/gqty'
import { formatAccessLevel } from '@hapana/shared/web/utils/staff-users.utils'

import { styles } from './user-access-styles'

export const USERS_STATUSES_OPTIONS = {
  active: {
    key: StaffUserStatus.active,
    label: t('core:userAccess.tabs.users.table.statuses.active'),
  },
  inactive: {
    key: StaffUserStatus.inactive,
    label: t('core:userAccess.tabs.users.table.statuses.inactive'),
  },
}
export const ACCESS_OPTIONS = {
  brand: {
    label: t('core:userAccess.tabs.users.table.brand'),
    key: 'ACCESS',
  },
  location: {
    label: t('core:userAccess.tabs.users.table.location'),
    key: 'CORPORATE',
  },
  noAccess: {
    label: t('core:userAccess.tabs.users.table.noAccess'),
    key: 'NO_ACCESS',
  },
}

export const USER_ACCESS_TABLE_DATA_STRUCTURE = {
  id: {
    key: 'id',
  },
  profile: {
    label: t('core:userAccess.tabs.users.table.columns.nameAndEmail'),
    key: 'profile',
    serverSideKey: 'name',
  },
  accessPolicies: {
    label: t('core:userAccess.tabs.users.table.columns.accessPolicies'),
    key: 'accessPolicies',
    serverSideKey: 'accessPolicies',
    serverSideFilterOperator: FILTER_OPERATORS.IN,
  },
  locationGroup: {
    label: t('core:userAccess.tabs.users.table.columns.locationGroup'),
    key: 'siteGroups',
    serverSideKey: 'siteGroups',
    serverSideFilterOperator: FILTER_OPERATORS.IN,
  },
  location: {
    label: t('core:userAccess.tabs.users.table.columns.location'),
    key: 'sites',
    serverSideKey: 'sites',
    serverSideFilterOperator: FILTER_OPERATORS.IN,
  },
  levelAccess: {
    label: t('core:userAccess.tabs.users.table.columns.levelAccess'),
    key: 'accessLevel',
    serverSideKey: 'accessLevel',
    serverSideFilterOptions: [
      {
        label: t('common:levelAccess.brand'),
        value: StaffUserAccessLevel.corporate,
      },
      {
        label: t('common:levelAccess.location'),
        value: StaffUserAccessLevel.sites,
      },
    ],
  },
  status: {
    label: t('core:userAccess.tabs.users.table.columns.status'),
    key: 'status',
    serverSideKey: 'status',
    serverSideFilterOptions: [
      {
        label: USERS_STATUSES_OPTIONS.active.label,
        value: USERS_STATUSES_OPTIONS.active.key,
      },
      {
        label: USERS_STATUSES_OPTIONS.inactive.label,
        value: USERS_STATUSES_OPTIONS.inactive.key,
      },
    ],
  },
}

export const TABLE_PROPS_USER_ACCESS = {
  columns: [
    {
      Header: USER_ACCESS_TABLE_DATA_STRUCTURE.profile.label,
      accessor: USER_ACCESS_TABLE_DATA_STRUCTURE.profile.key,
      customRowRender: (_, cell) => {
        const { name, email, profileImage } =
          cell.row.values[USER_ACCESS_TABLE_DATA_STRUCTURE.profile.key]
        return (
          <Box sx={commonStyles.verticallyCenteredFlexRow} gap={2}>
            <Avatar
              variant={AVATAR_VARIANT.CIRCULAR}
              avatarType={AVATAR_TYPES.SINGLE_AVATAR}
              size={AVATAR_SIZE.MEDIUM}
              src={profileImage ?? ''}
            />
            <Box sx={styles.profileBox}>
              <Typography
                variant="header4"
                typography="grayHeader4"
                color="gray.80"
                sx={styles.profileText}
              >
                {name}
              </Typography>
              <Typography
                variant="body1"
                typography="medium"
                color="gray.80"
                sx={styles.profileText}
              >
                {email}
              </Typography>
            </Box>
          </Box>
        )
      },
      hasSorting: true,
      serverSideKey: USER_ACCESS_TABLE_DATA_STRUCTURE.profile.serverSideKey,
    },
    {
      Header: USER_ACCESS_TABLE_DATA_STRUCTURE.accessPolicies.label,
      accessor: USER_ACCESS_TABLE_DATA_STRUCTURE.accessPolicies.key,
      customRowRender: (_, cell) => (
        <Typography variant="body2" color="textSecondary" maxWidth={200} textOverflow="ellipsis">
          {cell.row.values.accessPolicies}
        </Typography>
      ),
      hasFilter: true,
      filter: 'array',
      serverSideFilterOptions:
        USER_ACCESS_TABLE_DATA_STRUCTURE.accessPolicies.serverSideFilterOptions,
      serverSideKey: USER_ACCESS_TABLE_DATA_STRUCTURE.accessPolicies.serverSideKey,
    },
    {
      accessor: USER_ACCESS_TABLE_DATA_STRUCTURE.locationGroup.key,
      Header: USER_ACCESS_TABLE_DATA_STRUCTURE.locationGroup.label,
      isVisible: false,
      hasFilter: true,
      filter: 'array',
      filterSelectType: FILTER_SELECT_TYPES.select,
      serverSideFilterOptions:
        USER_ACCESS_TABLE_DATA_STRUCTURE.locationGroup.serverSideFilterOptions,
      serverSideKey: USER_ACCESS_TABLE_DATA_STRUCTURE.locationGroup.serverSideKey,
    },
    {
      accessor: USER_ACCESS_TABLE_DATA_STRUCTURE.location.key,
      Header: USER_ACCESS_TABLE_DATA_STRUCTURE.location.label,
      isVisible: false,
      hasFilter: true,
      filter: 'array',
      filterSelectType: FILTER_SELECT_TYPES.select,
      serverSideFilterOptions: USER_ACCESS_TABLE_DATA_STRUCTURE.location.serverSideFilterOptions,
      serverSideKey: USER_ACCESS_TABLE_DATA_STRUCTURE.location.serverSideKey,
    },
    {
      Header: USER_ACCESS_TABLE_DATA_STRUCTURE.levelAccess.label,
      accessor: 'accessLevel',
      hasFilter: true,

      filter: 'exact',
      filterSelectType: FILTER_SELECT_TYPES.select,
      serverSideFilterOptions: USER_ACCESS_TABLE_DATA_STRUCTURE.levelAccess.serverSideFilterOptions,
      serverSideKey: USER_ACCESS_TABLE_DATA_STRUCTURE.levelAccess.serverSideKey,
      mobileFilterType: MOBILE_FILTER_TYPES.radio,
      customRowRender: (_, cell) => {
        const { accessLevel } = cell.row.original
        const accessLevelLabels = formatAccessLevel(accessLevel, t)

        return (
          <Typography variant="body2" color="textSecondary">
            {accessLevelLabels}
          </Typography>
        )
      },
    },
    {
      accessor: USER_ACCESS_TABLE_DATA_STRUCTURE.status.key,
      Header: USER_ACCESS_TABLE_DATA_STRUCTURE.status.label,
      hasFilter: true,
      filterSelectType: FILTER_SELECT_TYPES.select,
      filter: 'exact',
      customRowRender: (_, cell) => {
        const cellValue = cell.row.values.status
        return <TableStatus status={cellValue} />
      },
      serverSideKey: USER_ACCESS_TABLE_DATA_STRUCTURE.status.serverSideKey,
      serverSideFilterOptions: USER_ACCESS_TABLE_DATA_STRUCTURE.status.serverSideFilterOptions,
      mobileFilterType: MOBILE_FILTER_TYPES.radio,
    },
  ],
}

export const MUI_SELECT_PROPS = {
  PaperProps: {
    style: {
      maxHeight: 300,
    },
  },
}
